import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Download, ExternalLink, FileText, X } from 'lucide-react';
import { documentPagePath, textOf, useViewerFile, ViewerBody } from './ViewerContent';
import styles from './document-viewer.module.css';

// A document opened over the current page, centred, with the page behind it
// dimmed and blurred. View only — no download or print. The file comes through
// the same permission- and tenant-checked download the Documents tab uses.
const DocumentViewer = ({
  documentId,
  open,
  onClose,
  title,
  fetchFile,
  ownTabHref,
  onDownload,
}) => {
  const [state, fail] = useViewerFile(documentId, open, fetchFile);
  // The viewer's own tab is a lender route and fetches through the lender's
  // download, so it is offered only where that is the caller. The borrower's
  // portal gets the viewer without it rather than a link that 403s.
  const newTabHref =
    ownTabHref === undefined ? documentPagePath(documentId, textOf(title)) : ownTabHref;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.overlay} />
        <DialogPrimitive.Content
          className={styles.content}
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => {
            // Esc in the search box closes the search, not the document.
            if (e.target?.closest?.('[data-viewer-search]')) e.preventDefault();
          }}
        >
          <header className={styles.header}>
            <FileText className={styles.fileIcon} aria-hidden="true" />
            <DialogPrimitive.Title className={styles.fileName}>{title || 'Document'}</DialogPrimitive.Title>
            {/* View only unless a caller says otherwise. The lender's Documents
                tab deliberately offers no download; the borrower's portal
                does, because forwarding the file to an agent is the whole
                point of their having it. The caller passes its own download
                so there is one implementation of it, named correctly. */}
            {onDownload && (
              <button
                type="button"
                className={styles.newTab}
                onClick={onDownload}
                aria-label="Download document"
                title="Download"
              >
                <Download aria-hidden="true" />
              </button>
            )}
            {documentId && newTabHref && (
              // The same view in a tab of its own. A link rather than a
              // script-opened window, so no popup blocker stands in the way
              // and a middle-click works as it does on any link.
              <a
                href={newTabHref}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.newTab}
                aria-label="Open in new tab"
                title="Open in new tab"
              >
                <ExternalLink aria-hidden="true" />
              </a>
            )}
            <DialogPrimitive.Close className={styles.close} aria-label="Close document">
              <X aria-hidden="true" />
            </DialogPrimitive.Close>
          </header>
          <div className={styles.body}>
            <ViewerBody state={state} onError={fail} name={textOf(title)} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default DocumentViewer;
